// The text rules a page title and meta description obey: the budgets, the
// brand suffix, and how each is cut when it will not fit. Shared by the head
// renderer and the admin editors so both measure against the same numbers.
//
// A leaf on purpose: `server-seo.ts` has module-scope side effects (frozen
// tables, zod schemas) the bundler cannot tree-shake, so importing it from a
// React component would ship them to the browser.

import { isBlank } from '@leanin/shared/visible-text';

export const SITE_NAME = 'Lean In Connect';

// Google truncates by rendered pixel width, not by character count: roughly
// 920px for a snippet and 580px for a title on desktop, both narrower on
// mobile. Character caps are therefore an approximation. 155 is the
// conservative end of the usual 155-160 range for a description. 60 is the
// top of the 50-60 range for a title because it is the last-resort cut, not
// the target: the editor counts against the point where the brand suffix
// stops fitting.
export const DESCRIPTION_MAX = 155;
export const TITLE_MAX = 60;

// A page title reads "Thing · Lean In Connect" so the brand is present
// without crowding out the distinctive part, which is what a searcher scans
// for.
export const TITLE_BRAND_SUFFIX = ` · ${SITE_NAME}`;

export function collapseWhitespace(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

// A hard cut counts UTF-16 code units, so it can land between the halves of
// a surrogate pair and leave an orphaned high half at the end, which renders
// as a replacement character. Matching only D800-DBFF leaves an intact pair
// (whose last unit is a low half) untouched.
const DANGLING_HIGH_SURROGATE = /[\uD800-\uDBFF]$/;

// Cuts at the last word boundary inside `max`, appending an ellipsis. The
// 0.6 floor guards the degenerate case of a single very long token: rather
// than emit one character and an ellipsis, take the hard cut.
//
// `max` is the budget for the WHOLE result, ellipsis included, so the cut
// reserves that one character. Slicing to `max` and then appending would
// return max + 1 and quietly overrun the limit it is supposed to enforce.
//
// A budget of zero is answered before `room` is computed: a negative `room`
// would make `slice` count back from the end.
export function clampAtWord(text: string, max: number): string {
  if (text.length <= max) return text;
  if (max <= 0) return '';
  const room = max - 1;
  const cut = text.slice(0, room);
  const lastSpace = cut.lastIndexOf(' ');
  const body = lastSpace > room * 0.6 ? cut.slice(0, lastSpace) : cut;
  // Strip trailing punctuation before the dangling-surrogate check, because
  // stripping can expose a high surrogate that was not last before.
  return `${body.replace(/[,;:.\s]+$/, '').replace(DANGLING_HIGH_SURROGATE, '')}…`;
}

// A period that ends a common abbreviation does not end a sentence, so a cut
// there would publish "...an interview with Dr." as a whole thought.
const ABBREVIATION_BEFORE_CUT = /\b(?:Mr|Mrs|Ms|Mx|Dr|Prof|Sr|Jr|St|vs|etc|e\.g|i\.e|U\.S)\.$/i;

// A full stop, question mark or exclamation mark followed by whitespace.
const SENTENCE_END = /[.!?](?=\s)/g;

// The longest run of whole sentences from the start of `text` that fits in
// `max`, or null when not even the first sentence fits.
export function leadingSentences(text: string, max: number): string | null {
  if (text.length <= max && /[.!?]$/.test(text)) return text;
  let end = -1;
  for (const match of text.matchAll(SENTENCE_END)) {
    const candidate = match.index + 1;
    if (candidate > max) break;
    if (ABBREVIATION_BEFORE_CUT.test(text.slice(0, candidate))) continue;
    end = candidate;
  }
  return end === -1 ? null : text.slice(0, end);
}

// Cuts at the last sentence end inside `max`, so a snippet reads as finished
// thoughts rather than a phrase trailing into an ellipsis. Falls back to the
// word cut when the last whole sentence would leave less than half the
// budget, because a short complete sentence then says less than a longer
// clipped one.
export function clampAtSentence(text: string, max: number): string {
  if (text.length <= max) return text;
  const sentences = leadingSentences(text, max);
  return sentences !== null && sentences.length >= max / 2
    ? sentences
    : clampAtWord(text, max);
}

// For plain-text sources: the description columns, and the head's own copy.
export function clampDescription(text: string): string {
  return clampAtSentence(collapseWhitespace(text), DESCRIPTION_MAX);
}

// How a resource's fallback names what it is, in English because the head
// is always rendered in English. A kind this deploy does not know reads as a
// resource.
const KIND_NOUNS: Readonly<Record<string, string>> = Object.freeze({
  article: 'an article',
  video: 'a video',
  toolkit: 'a toolkit',
  workshop: 'a workshop',
  essay: 'an essay',
  discussion_guide: 'a discussion guide',
  research: 'research',
  podcast: 'a podcast',
  activity: 'an activity',
  playlist: 'a playlist',
});

// The call to action a fallback snippet closes on, by what the reader does
// with the resource.
const KIND_CALLS: Readonly<Record<string, string>> = Object.freeze({
  article: 'Read it free on Lean In Connect.',
  essay: 'Read it free on Lean In Connect.',
  research: 'Read it free on Lean In Connect.',
  video: 'Watch it free on Lean In Connect.',
  podcast: 'Listen free on Lean In Connect.',
  toolkit: 'Get it free on Lean In Connect.',
  workshop: 'Get it free on Lean In Connect.',
  discussion_guide: 'Get it free on Lean In Connect.',
  activity: 'Get it free on Lean In Connect.',
});
const DEFAULT_CALL = 'Explore it free on Lean In Connect.';
const TRACK_CALL = 'Start the free track on Lean In Connect.';

function byKind(table: Readonly<Record<string, string>>, kind: string | null | undefined, fallback: string): string {
  return kind != null && Object.hasOwn(table, kind) ? table[kind]! : fallback;
}

function nonBlank(value: string | null | undefined): string | null {
  return value != null && value.trim() !== '' ? collapseWhitespace(value) : null;
}

export interface SearchCopy {
  // The `<title>`, brand included.
  readonly title: string;
  // The snippet, within `DESCRIPTION_MAX`.
  readonly description: string;
  // The page's summary without the call to action, for structured data,
  // which describes the page rather than asking for the click.
  readonly descriptionSource: string;
}

// A summary closed by a call to action, inside the snippet budget. Whole
// sentences of the summary are kept; when not even its first sentence fits
// alongside the call, the summary is cut alone, since a clipped sentence
// followed by a call to action reads as a mistake.
function summaryWithCall(summary: string, call: string): string {
  const room = DESCRIPTION_MAX - call.length - 1;
  const sentences = leadingSentences(summary, room);
  return sentences === null ? clampDescription(summary) : `${sentences} ${call}`;
}

// "<name>: <what it is> from Lean In." with the name cut to whatever room the
// call to action leaves, and dropped when blank.
function namedFallback(name: string, what: string, call: string): string {
  const tail = ` ${call}`;
  if (name === '') return `${capitalize(what)}.${tail}`;
  const room = DESCRIPTION_MAX - tail.length - `: ${what}.`.length;
  return `${clampAtWord(name, room)}: ${what}.${tail}`;
}

// What a resource page tells search and link previews. The head renderer and
// the admin editor's preview both call this, so what an editor sees is what
// ships.
//
// The editor's search copy wins: a written synopsis with a call to action is
// what earns the click, and nothing generated here matches it. Without one,
// the lede (an editor-written summary) is closed with a call to action that
// fits the kind. The body is never used: its opening is an introduction, not
// a synopsis.
export function resourceSearchCopy(resource: {
  readonly title: string;
  readonly kind?: string | null;
  readonly seoTitle?: string | null;
  readonly seoDescription?: string | null;
  readonly lede?: string | null;
}): SearchCopy {
  const name = collapseWhitespace(resource.title);
  const call = byKind(KIND_CALLS, resource.kind, DEFAULT_CALL);
  const searchCopy = nonBlank(resource.seoDescription);
  const lede = nonBlank(resource.lede);
  const what = `${byKind(KIND_NOUNS, resource.kind, 'a resource')} from Lean In`;
  const description =
    searchCopy !== null
      ? clampDescription(searchCopy)
      : lede !== null
        ? summaryWithCall(lede, call)
        : namedFallback(name, what, call);
  return {
    title: composeTitle(editorialTitleSubject(resource)),
    description,
    descriptionSource:
      searchCopy ?? lede ?? (name === '' ? `${capitalize(what)}.` : `${name}: ${what}.`),
  };
}

// What a learning track page tells search and link previews. Same precedence
// and the same single caller contract as `resourceSearchCopy`: search copy,
// then the track's description closed with a call to action, then a sentence
// naming the track and how many resources a visitor can open in it.
export function trackSearchCopy(track: {
  readonly title: string;
  readonly seoTitle?: string | null;
  readonly seoDescription?: string | null;
  readonly description?: string | null;
  readonly resourceCount?: number | null;
}): SearchCopy {
  const name = collapseWhitespace(track.title);
  const count = track.resourceCount ?? 0;
  const what = `a Lean In learning track${count > 0 ? ` of ${count} resource${count === 1 ? '' : 's'}` : ''}`;
  const searchCopy = nonBlank(track.seoDescription);
  const summary = nonBlank(track.description);
  const description =
    searchCopy !== null
      ? clampDescription(searchCopy)
      : summary !== null
        ? summaryWithCall(summary, TRACK_CALL)
        : namedFallback(name, what, TRACK_CALL);
  return {
    title: composeTitle(editorialTitleSubject(track)),
    description,
    descriptionSource:
      searchCopy ?? summary ?? (name === '' ? `${capitalize(what)}.` : `${name}: ${what}.`),
  };
}

function capitalize(text: string): string {
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}

// The brand suffix is the first thing sacrificed when the whole title will
// not fit. A Circle name is what a searcher recognises and what they typed;
// the site name is the redundant half, and Google frequently appends the site
// name itself anyway. Truncating the name to preserve " · Lean In Connect"
// would throw away the useful half to keep the useless one. Only a subject
// that exceeds the cap on its own gets cut, and then at a word boundary.
export function composeTitle(subject: string | null): string {
  const trimmed = collapseWhitespace(subject ?? '');
  if (trimmed === '') return SITE_NAME;
  const withBrand = `${trimmed}${TITLE_BRAND_SUFFIX}`;
  if (withBrand.length <= TITLE_MAX) return withBrand;
  if (trimmed.length <= TITLE_MAX) return trimmed;
  return clampAtWord(trimmed, TITLE_MAX);
}

// The subject a resource or learning track page is titled by: the editor's
// search title when one is written, else the entity's own title. The head
// renderer and the SPA's tab title both read it, so a page's crawled title
// and its in-app title are one string. Blank counts as absent, as it does for
// every other editor-written search field.
export function editorialTitleSubject(entity: {
  readonly title: string;
  readonly seoTitle?: string | null;
}): string {
  return firstNonEmpty(entity.seoTitle) ?? entity.title;
}

// An editor field's value as a save sends it: blank (by `isBlank`, the rule
// the API's boundary applies) is null, which clears the field, and anything
// else is trimmed. The API decides what is stored, but sending what it will
// store keeps an editor's dirty check and its reset-from-response in step
// with the saved row.
export function blankToNull(value: string): string | null {
  return isBlank(value) ? null : value.trim();
}

// Whether two values of an editor field are the same copy: the same once each
// is normalised as a save sends it (`blankToNull`). A stored value is compared
// the same way: the column's check admits a value of whitespace or format
// characters only, which a save sends as null.
export function sameSeoCopy(a: string | null, b: string | null): boolean {
  return blankToNull(a ?? '') === blankToNull(b ?? '');
}

// The first candidate with visible text. Blank counts as absent everywhere a
// field is optional copy: an empty string would otherwise publish `content=""`
// or a bare brand title, and nothing downstream rejects either.
export function firstNonEmpty(...candidates: readonly (string | null | undefined)[]): string | null {
  for (const candidate of candidates) {
    if (candidate != null && !isBlank(candidate)) return candidate;
  }
  return null;
}
